/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {QueryClient} from 'react-query';
import {DefaultOptions} from 'react-query/types';

/**
 * Defines the default Cache Expiry time which is used when configuring Query
 * Client instances as well as when persisting the Query Cache. Defaults to
 * 5 minutes.
 */
export const DEFAULT_CACHE_EXPIRY = (5 * 60 * 1000);

/**
 * Defines the default Query Client configuration to be used by all iOn Class
 * of Applications.
 *
 * The following configurations are provided as defaults:
 *   - refetchOnWindowFocus: disabled
 *   - cacheTime: 5 minutes (to be override at the query level as needed)
 *   - retry: 3  (to be override at the query level as needed)
 */
export const queryDefaultOptions = {
  refetchOnWindowFocus : false,
  cacheTime            : DEFAULT_CACHE_EXPIRY,
  retry                : 3
};

/**
 * Defines the QueryClient instance used by the application when configuring
 * the QueryClientProvider.
 */
let client: QueryClient;

/**
 * Returns the `QueryClient` instance which is to be used when configuring
 * the top-level application component (generally, `_app.tsx`). The client
 * is a pseudo Singleton instance in that it is only instantiated once.
 *
 * ```typescript
 *
 * import {getQueryClient} from '@/ion/query/src/config';
 * import {ReactQueryDevtools} from 'react-query/devtools';
 * import {isDevMode} from '@ion/applications/src/utils/env';
 * ...
 *
 *  <QueryClientProvider client={getQueryClient()}>
 *    ...
 *    {isDevMode() && <ReactQueryDevtools initialIsOpen={false} />}
 *  </QueryClientProvider>
 * ```
 *
 * Generally, this function should only be needed when the top-level application
 * provider is being initialized. Otherwise, the useQueryClient hook should be
 * used for accessing the client instance.
 *
 * @see https://react-query.tanstack.com/guides/important-defaults
 *
 */
export const getQueryClient = (options?: DefaultOptions, override = false): QueryClient => {
  if (!client) {
    client = new QueryClient({
      defaultOptions : {
        queries : {
          ...queryDefaultOptions
        },
        ...options
      }
    });
  }
  if (override) {
    client.setDefaultOptions(options as DefaultOptions);
  }
  return client;
};

export default getQueryClient;
